import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  MegaphoneIcon,
  UserGroupIcon,
  PresentationChartLineIcon,
  BeakerIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const MetaAdsPage: React.FC = () => {
  const navigate = useNavigate();

  // SEO setup
  useSEO(pageConfigs.metaAds);

  // Navigation handlers
  const handleGetAudit = () => {
    navigate('/consultation');
  };

  const handleViewCaseStudies = () => {
    navigate('/case-studies');
  };

  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };

  const handleGetStarted = () => {
    navigate('/quote');
  };

  const handleLearnMore = () => {
    navigate('/contact');
  };
  const features = [
    {
      title: "Campaign Strategy",
      description: "Develop comprehensive Meta advertising strategies aligned with your business goals and target audience.",
      icon: <MegaphoneIcon className="w-8 h-8" />,
      benefits: ["Goal-based planning", "Budget allocation", "Platform selection", "Campaign structure"]
    },
    {
      title: "Audience Targeting",
      description: "Precision targeting to reach your ideal customers based on demographics, interests, and behaviors.",
      icon: <UserGroupIcon className="w-8 h-8" />,
      benefits: ["Custom audiences", "Lookalike audiences", "Interest targeting", "Behavioral targeting"]
    },
    {
      title: "Ad Creative Development",
      description: "Create compelling ad creative that drives engagement and conversions across Meta platforms.",
      icon: <BeakerIcon className="w-8 h-8" />,
      benefits: ["Image ads", "Video ads", "Carousel ads", "Collection ads"]
    },
    {
      title: "Testing & Optimization",
      description: "Continuous testing and optimization to maximize performance and return on ad spend.",
      icon: <PresentationChartLineIcon className="w-8 h-8" />,
      benefits: ["A/B testing", "Performance analysis", "Budget optimization", "ROAS improvement"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Account Analysis",
      description: "We analyze your current Meta ads performance, account structure, and historical data."
    },
    {
      step: "02",
      title: "Strategy Development",
      description: "Create a comprehensive Meta advertising strategy tailored to your business goals."
    },
    {
      step: "03",
      title: "Implementation",
      description: "Set up campaigns, ad sets, and creative with precision targeting and messaging."
    },
    {
      step: "04",
      title: "Optimization & Scaling",
      description: "Continuously monitor, test, and optimize campaigns to maximize ROAS."
    }
  ];

  const caseStudy = {
    client: "E-commerce Fashion Brand",
    industry: "Retail",
    challenge: "High ad costs and low return on ad spend (ROAS) on Facebook and Instagram",
    solution: "Implemented strategic audience targeting, creative testing, and campaign optimization",
    results: [
      "3.2x improvement in ROAS",
      "45% reduction in cost per acquisition",
      "185% increase in conversion rate",
      "250% increase in revenue from Meta ads"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.metaAds} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                <MegaphoneIcon className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-semibold text-sm">Meta Advertising Experts</span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
                Strategic 
                <span className="text-blue-600"> Meta Ads Management</span>
              </h1>
              
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                Maximize your ROI with strategic Facebook and Instagram advertising campaigns.
                Our data-driven approach delivers measurable results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={handleGetAudit}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Get Meta Ads Audit</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleViewCaseStudies}
                  className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View Case Studies
                </button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">3.2x</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">ROAS Improvement</div>
                  <div className="text-xs text-slate-500">Average for clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">45%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">CPA Reduction</div>
                  <div className="text-xs text-slate-500">Cost per acquisition</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">185%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Conversion Rate</div>
                  <div className="text-xs text-slate-500">Increase</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">250%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Revenue Growth</div>
                  <div className="text-xs text-slate-500">From Meta ads</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <MegaphoneIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Meta Ads Audit</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get a comprehensive analysis of your current Meta ads performance and 
                  receive a customized strategy to improve your results.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Account structure review</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Audience targeting analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Custom optimization recommendations</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleGetAudit}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Get Your Free Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Comprehensive Meta Ads Management
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our strategic approach maximizes your return on ad spend across Facebook and Instagram
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{feature.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Our 4-Step Process
            </h2>
            <p className="text-xl text-slate-600">
              Proven methodology for Meta advertising success
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
                  <StarIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                    <p className="text-slate-600">{caseStudy.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution</h4>
                    <p className="text-slate-600">{caseStudy.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Results</h4>
                    <div className="space-y-2">
                      {caseStudy.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckIcon className="w-5 h-5 text-green-600" />
                          <span className="text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">📊</div>
                  <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">Ready to maximize your Meta ads ROI?</h3>
                  <p className="text-slate-600">Schedule a consultation with our Meta ads experts</p>
                </div>
                
                <button 
                  onClick={handleScheduleConsultation}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors mb-4"
                >
                  Schedule Consultation
                </button>
                
                <button 
                  onClick={handleViewCaseStudies}
                  className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View More Case Studies
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Meta Platforms We Manage
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive advertising management across all Meta platforms
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📘</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Facebook</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📸</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Instagram</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">💬</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Messenger</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🌐</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Audience Network</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Types Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Ad Types We Optimize
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Strategic management of all Meta ad formats for maximum performance
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🖼️</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Image Ads</h3>
              <p className="text-slate-600">Eye-catching static images with compelling copy</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🎬</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Video Ads</h3>
              <p className="text-slate-600">Engaging video content that tells your story</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Carousel Ads</h3>
              <p className="text-slate-600">Multiple images or videos in a single ad</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🛍️</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Collection Ads</h3>
              <p className="text-slate-600">Showcase products from your catalog</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">📱</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Stories Ads</h3>
              <p className="text-slate-600">Full-screen vertical format for immersive experiences</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-2">Dynamic Ads</h3>
              <p className="text-slate-600">Personalized ads based on user behavior</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-display font-bold mb-6">
              Ready to Maximize Your Meta Advertising ROI?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Get started with a free Meta ads audit and custom strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleGetStarted}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors"
              >
                Get Started
              </button>
              <button 
                onClick={handleLearnMore}
                className="bg-transparent hover:bg-white/10 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/20 transition-colors"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MetaAdsPage;